import { fetchWithChatFeedback } from './chatFeedbackRequired';
/**
 * API client configuration for FastAPI backend
 */

import {
  normalizeApiError,
  USER_FACING_ERROR_MESSAGES,
} from '../utils/userFacingError';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export interface ApiResponse<T> {
  success: boolean;
  status?: number;
  data?: T;
  error?: string;
  error_code?: string;        // NEW — internal use only, never rendered
  technical_detail?: string;  // NEW — internal use only, never rendered
  /** A mutation may have committed before its response became unavailable. */
  request_outcome_unknown?: boolean;
  required_stage?: number;    // Internal routing recovery metadata
  requested_stage?: number;   // Internal routing recovery metadata
  feature?: string;           // Internal feature identifier
  message?: string;
  /**
   * Optional count of distinct product types set by Stage 1
   * (`POST /api/requirements/questions`). Defaults to 1 (single product)
   * when absent. Passed through as-is from the JSON response so downstream
   * UI code can surface a warning banner when the value is greater than 1.
   */
  num_items_detected?: number;
}

/**
 * Generic API call function with error handling
 */
export async function apiCall<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
  body?: any,
  opts?: {
    timeoutMs?: number;
    retries?: number;
    retryDelayMs?: number;
    /** Preserve uncertain mutation completion without exposing transport details. */
    mutation?: boolean;
    /** Extra headers for endpoint-specific contracts, e.g. idempotency keys. */
    headers?: Record<string, string>;
    /** Cancel a caller-owned read without retrying the cancelled snapshot. */
    signal?: AbortSignal;
  }
): Promise<ApiResponse<T>> {
  const timeoutMs = opts?.timeoutMs ?? 15 * 60 * 1000; // default 15 minutes
  const maxRetries = opts?.retries ?? 2; // retry twice on network errors
  const retryDelayMs = opts?.retryDelayMs ?? 2000;

  let attempt = 0;
  while (true) {
    attempt++;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let cancelledByCaller = false;
    let httpFailureObserved = false;
    const abortFromCaller = () => {
      if (!controller.signal.aborted) cancelledByCaller = true;
      controller.abort();
    };
    opts?.signal?.addEventListener('abort', abortFromCaller, { once: true });
    if (opts?.signal?.aborted) abortFromCaller();
    try {
      if (controller.signal.aborted) {
        throw new DOMException('Request cancelled', 'AbortError');
      }
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...opts?.headers,
      };

      const options: RequestInit = {
        method,
        headers,
        signal: controller.signal,
        credentials: 'include',
      };

      if (body && method !== 'GET') {
        options.body = JSON.stringify(body);
      }

      const response = await fetchWithChatFeedback(`${API_BASE_URL}${endpoint}`, options);
      httpFailureObserved = !response.ok;
      if (controller.signal.aborted) {
        throw new DOMException('Request cancelled', 'AbortError');
      }

      if (response.status === 401) {
        window.dispatchEvent(new Event('auth-expired'));
      }

      if (!response.ok) {
        // Try to extract detailed error message from response. Never expose
        // raw HTTP status codes or other technical noise to the user — fall
        // back to a friendly generic message when the body isn't a valid
        // ApiResponse / FastAPI error envelope.
        let errorMessage: string = USER_FACING_ERROR_MESSAGES.unrecognized;
        let errorCode: string | undefined;
        let technicalDetail: string | undefined;
        let requiredStage: number | undefined;
        let requestedStage: number | undefined;
        let feature: string | undefined;
        let errorData: { data?: T } | undefined;
        try {
          errorData = await response.json();
          const normalized = normalizeApiError(errorData);
          errorMessage = normalized.message;
          errorCode = normalized.errorCode;
          technicalDetail = normalized.technicalDetail;
          requiredStage = normalized.requiredStage;
          requestedStage = normalized.requestedStage;
          feature = normalized.feature;
        } catch (parseError) {
          // If JSON parsing fails, keep the generic friendly message.
          console.warn('Failed to parse error response:', parseError);
        }
        const err = new Error(errorMessage) as Error & {
          status?: number;
          error_code?: string;
          technical_detail?: string;
          required_stage?: number;
          requested_stage?: number;
          feature?: string;
          response_data?: T;
        };
        err.status = response.status;
        if (errorCode) err.error_code = errorCode;
        if (technicalDetail) err.technical_detail = technicalDetail;
        if (requiredStage !== undefined) err.required_stage = requiredStage;
        if (requestedStage !== undefined) err.requested_stage = requestedStage;
        if (feature) err.feature = feature;
        // A revision-conflict response may safely carry the current
        // server-authoritative projection. Preserve it for callers that can
        // rehydrate a stale editor; ordinary error consumers remain unchanged.
        if (errorData?.data !== undefined) err.response_data = errorData.data as T;
        throw err;
      }

      const data = await response.json();
      if (controller.signal.aborted) {
        throw new DOMException('Request cancelled', 'AbortError');
      }
      return data;
    } catch (error: any) {
      clearTimeout(timer);
      const isNetworkError = error?.name === 'AbortError' || error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError');
      const canRetry = isNetworkError && !error?.startupReadCancelled && !opts?.signal?.aborted
        && attempt <= maxRetries;
      if (!(cancelledByCaller && !httpFailureObserved && error?.name === 'AbortError')) {
        console.error(`API call failed (attempt ${attempt}/${maxRetries + 1}):`, error);
      }
      if (canRetry) {
        await delay(retryDelayMs * attempt); // simple backoff
        continue;
      }
      // Preserve error_code / technical_detail attached by the !response.ok
      // path above so downstream callers (and the friendly-error rendering
      // pipeline) keep the categorization.
      const errCode = (error as any)?.error_code;
      const errDetail = (error as any)?.technical_detail;
      const requiredStage = (error as any)?.required_stage;
      const requestedStage = (error as any)?.requested_stage;
      const feature = (error as any)?.feature;
      const responseData = (error as any)?.response_data;
      const status = (error as any)?.status;
      const out: ApiResponse<T> = {
        success: false,
        // Browser transport failures never reached a backend endpoint, so they
        // cannot establish any notification/incident state. Keep their copy
        // neutral even though the browser's raw Error message is technical.
        error: isNetworkError
          ? USER_FACING_ERROR_MESSAGES.unrecognized
          : error instanceof Error
            ? error.message
            : USER_FACING_ERROR_MESSAGES.unrecognized,
      };
      if (opts?.mutation && !httpFailureObserved) out.request_outcome_unknown = true;
      if (typeof status === 'number') out.status = status;
      if (errCode) out.error_code = errCode;
      if (errDetail) out.technical_detail = errDetail;
      if (typeof requiredStage === 'number') out.required_stage = requiredStage;
      if (typeof requestedStage === 'number') out.requested_stage = requestedStage;
      if (typeof feature === 'string' && feature) out.feature = feature;
      if (responseData !== undefined) out.data = responseData;
      return out;
    } finally {
      clearTimeout(timer);
      opts?.signal?.removeEventListener('abort', abortFromCaller);
    }
  }
}

/**
 * Add a delay for demo purposes (remove in production)
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Coalesce concurrent 401 follow-ups. A transient request that misses a
// long-lived cookie must not immediately eject the user from the application.
let sessionVerifyInFlight: Promise<void> | null = null;

export function verifySessionOrShowGate(onExpired: () => void): Promise<void> {
  if (sessionVerifyInFlight) return sessionVerifyInFlight;

  const verification = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        credentials: 'include',
      });
      if (response.status === 200) {
        localStorage.setItem('farchat_has_session', '1');
        return;
      }
    } catch {
      // A failed verification is handled as a genuine expired session below.
    }
    onExpired();
  })();

  // Keep the shared promise until the verification settles so bursts of 401s
  // coalesce into one cookie check. `finally` also guarantees a later 401 can
  // start a fresh verification, including when fetch unexpectedly rejects.
  const sharedVerification = verification.finally(() => {
    if (sessionVerifyInFlight === sharedVerification) {
      sessionVerifyInFlight = null;
    }
  });
  sessionVerifyInFlight = sharedVerification;
  return sharedVerification;
}
